import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  KeyboardSensor,
  type CollisionDetection,
  pointerWithin,
  useDndContext,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type AssignmentWarning,
  type BoardColumn as BoardColumnData,
  type BoardView,
  type TaskPriority,
  type TaskView,
  type UpdateStatusInput,
  type WorkflowCategory,
  entersWork,
} from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { EmptyState, ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { useAuth } from '../../auth/auth-context';
import { TaskDetailDrawer } from '../../collaboration/components/task-detail-drawer';
import { GitTaskMarksProvider } from '../../git/components/git-task-mark';
import { ProgressReviewWarningsProvider } from '../../git/components/progress-review-badge';
import { TaskProgressSlot } from '../../tasks/components/task-progress-slot';
import {
  type BoardFilter,
  NO_BOARD_FILTER,
  dimmedIdsOf,
  isFilterActive,
  blocksStart,
  matchesFilter,
  warnsBeforeStart,
} from '../filter';
import { todayIso } from '../../../lib/overdue';
import { TaskFormDialog } from '../../tasks/components/task-form-dialog';
import { useCompleteTaskGuard } from '../../tasks/use-complete-task-guard';
import { BacklogActions } from './backlog-column-actions';
import { mergeBacklogColumn } from '../backlog-merge';
import { sprintColor, sprintTint } from '../../sprints/color';
import { defaultSprintRange } from '../../sprints/range';
import { boardApi, boardKeys } from '../api';
import { taskFormApi, taskFormKeys } from '../../tasks/api';
import {
  applyMove,
  persistedDropIndex,
  columnGroupOf,
  columnOf,
  slotIndexOf,
  toRemovalIndex,
  findTask,
  reorderColumnWithinGroup,
} from '../reorder';
import { BoardColumn } from './board-column';
import { MilestoneStrip, milestonesOf } from './milestone-strip';
import { AddColumn, ColumnMenu } from './column-manager';
import { TaskCard } from './task-card';
import { displayDateRange } from '../../../lib/date';
import { canMoveBetween } from '../../tasks/status-flags';

/**
 * Id giả của cột Backlog.
 *
 * Không phải uuid có chủ đích: nó **không** là một `workflow_status`, và mọi
 * chỗ so sánh với id cột sẽ nhận ra ngay đây là cột ảo. Ba nhóm trạng thái
 * (`todo`/`in_progress`/`done`) giữ nguyên vì burndown, cycle time và CFD đọc
 * chính chúng — thêm một nhóm thứ tư vào enum là sửa lại mọi phép đo đã có.
 * Backlog là "chưa vào sprint" (`sprint_id IS NULL`), một câu hỏi khác hẳn.
 */
const BACKLOG_ID = 'backlog';

/**
 * Hiệu ứng "đáp xuống" chạy bao lâu, ms — APM-BOARD-land.
 *
 * ⚠️ Phải khớp `animation-duration` của `.board-land` trong `styles/index.css`.
 * Ngắn hơn thì hiệu ứng bị cắt giữa chừng khi `landedId` bị xoá; dài hơn thì
 * thẻ giữ lớp ấy sau khi đã đứng yên, và một cú thả tiếp vào cùng thẻ sẽ không
 * chạy lại được (React thấy `className` không đổi nên không khởi động lại
 * animation).
 */
const LAND_MS = 1_500;

/** Hai vùng thả cho thao tác kéo **cột**. Tiền tố để không lẫn với id cột thật. */

/**
 * Cú thả rơi vào vùng nào.
 *
 * Thả trúng một **cột** cũng tính là thả vào vùng chứa cột đó: người dùng nhắm
 * vào khoảng giữa hai cột chứ không nhắm vào nền của khung, và bắt họ trúng
 * đúng nền là biến một thao tác kéo thành một trò chơi.
 */
/**
 * Kéo thẻ trên board: con trỏ đang chỉ vào **khe** nào — APM-BOARD-slot2.
 *
 * ## `pointerWithin`, không phải `closestCorners`
 *
 * Board dùng `DragOverlay`, nên thẻ gốc **không di chuyển**: nó thu về cao 0px
 * tại chỗ cũ và chỉ có lớp phủ bay theo con trỏ. `closestCorners` so bằng hình
 * chữ nhật của thứ đang kéo, mà hình ấy đứng yên — nên người thắng cũng đứng
 * yên. Đo trên trình duyệt thật (APM-BOARD-i): suốt 44 lượt dò của một cú kéo
 * đi 190px, `over` không đổi lấy một lần.
 *
 * `pointerWithin` so bằng **toạ độ con trỏ**, thứ duy nhất thực sự di chuyển.
 *
 * ## Khe, không phải thẻ
 *
 * Xem docblock của `slotIndexOf`: câu hỏi là "chen vào khe nào", và khe thì
 * không phụ thuộc chiều kéo.
 */
export function boardCollision(
  args: Parameters<CollisionDetection>[0],
): ReturnType<CollisionDetection> {
  const containers = args.droppableContainers;

  const columns = containers.filter(isColumnContainer);
  const cards = containers.filter((container) => !isColumnContainer(container));

  const onColumn = pointerWithin({ ...args, droppableContainers: columns });
  if (onColumn.length === 0) return [];

  const before = firstCardAfterPointer(args, cards);
  return before === null ? onColumn : [{ id: before }];
}

/**
 * Cột tự khai `kind: 'column'` ở `useDroppable` — id của cột và id của thẻ đều
 * là UUID nên không có cách nào phân biệt chúng bằng chính chuỗi id.
 */
function isColumnContainer(container: { data?: { current?: unknown } }): boolean {
  const data = container.data?.current as { kind?: string } | undefined;
  return data?.kind === 'column';
}

/**
 * Thẻ đầu tiên có **đường giữa** nằm dưới con trỏ — chỗ chen vào nằm ngay trước nó.
 *
 * Lọc theo bề ngang (`left`/`right` bao con trỏ) thay vì theo danh sách thành
 * viên của cột: hai cột không bao giờ chồng nhau theo phương ngang, nên phép
 * lọc ấy đủ để không bao giờ chọn nhầm thẻ của cột bên cạnh — và nó không cần
 * cột phải khai ra nó có những thẻ nào.
 *
 * ⚠️ So bằng **đường giữa**, không phải mép trên. Với mép trên thì cả thân thẻ
 * mang một nghĩa duy nhất ("chen trước tôi"), và nửa dưới của thẻ cuối cùng
 * cũng thế — nên không có chỗ nào trên màn hình nghĩa là "chen sau thẻ này".
 */
function firstCardAfterPointer(
  args: Parameters<CollisionDetection>[0],
  cards: Parameters<CollisionDetection>[0]['droppableContainers'],
): string | number | null {
  /*
    Kéo bằng **bàn phím** không có `pointerCoordinates` — dnd-kit chỉ đặt nó cho
    sensor con trỏ. Rơi về tâm của `collisionRect` (hình của thẻ đang bay): với
    bàn phím thì đó đúng là "chỗ người dùng đang chỉ tới".

    Trả `null` ở đây như bản đầu nghĩa là mọi cú kéo bằng bàn phím đều rơi vào
    nhánh cột ⇒ luôn "xuống cuối", im lặng.
  */
  const pointer = args.pointerCoordinates ?? {
    x: args.collisionRect.left + args.collisionRect.width / 2,
    y: args.collisionRect.top + args.collisionRect.height / 2,
  };

  let best: { id: string | number; top: number } | null = null;
  for (const card of cards) {
    // Thẻ đang kéo đã thu về cao 0px và số đo của nó là số đo **trước** lúc
    // nhấc — bỏ qua, nếu không nó tự thắng rồi `targetOf` trả `null`, và khung
    // trống đứng im ở chỗ cũ.
    if (card.id === args.active.id) continue;

    const rect = args.droppableRects.get(card.id);
    if (!rect) continue;
    if (pointer.x < rect.left || pointer.x > rect.right) continue;
    if (pointer.y >= rect.top + rect.height / 2) continue;

    if (!best || rect.top < best.top) best = { id: card.id, top: rect.top };
  }
  return best?.id ?? null;
}

/** Bốn thao tác cột, gộp vào một mutation để chỉ có một chỗ nạp lại dữ liệu. */
type ColumnAction =
  | { kind: 'create'; input: { name: string; category: WorkflowCategory; sprintId?: string } }
  | { kind: 'update'; statusId: string; input: UpdateStatusInput }
  | { kind: 'reorder'; statusIds: string[] }
  | { kind: 'delete'; statusId: string };

interface MoveVariables {
  readonly taskId: string;
  readonly statusId: string;
  readonly index: number;
  readonly sameColumn: boolean;
  readonly serverIndex?: number;
  /** Giờ bắt đầu thực tế khai lại ở hộp "chưa đủ thời gian tối thiểu" — APM-TMIN-b. */
  readonly actualStartAt?: string;
}

interface MoveResult {
  readonly warnings: AssignmentWarning[];
}

interface BoardProps {
  readonly projectId: string;
  readonly sprintId?: string;
  readonly all?: boolean;
  /** `project:update` ở dự án này — duyệt gợi ý AI trong form việc. */
  readonly canEditColumns?: boolean;
  /**
   * `workflow:manage` — menu ••• của cột và nút thêm cột. Tách khỏi
   * `canEditColumns` để PM cấp riêng cho Thành viên (người dùng chốt 2026-10-14).
   */
  readonly canManageColumns?: boolean;
  /**
   * `task:update` ở dự án này — mở ô cập nhật tiến độ và nút Đóng việc trong
   * popup chi tiết (APM-TASK-i).
   *
   * Trước đây drawer ở đây nhận `canEdit` **cắm cứng `true`**: board vốn chỉ
   * hiện cho người sửa được, nên nó đúng trong thực tế và sai về nguyên tắc.
   * Nay có nút gọi API thật trong popup nên câu hỏi phải được hỏi thật — người
   * xem sẽ thấy nút, bấm, và nhận 403.
   */
  readonly canEditTasks?: boolean;
  /**
   * `project:update` — kéo thả thẻ trên board (APM-CHANGE-b).
   *
   * Kéo một thẻ là thao tác **lập kế hoạch**: đổi trạng thái, đổi thứ tự, và ở
   * cột backlog thì đổi cả sprint. Chỉ PM. Người khác vẫn mở popup, báo tiến độ
   * và đóng việc — cửa ấy hỏi `task:update`, một câu hỏi khác.
   */
  readonly canSchedule?: boolean;
  /**
   * `task:assign` — bấm vào tên người phụ trách ở chân thẻ để giao lại việc
   * (người dùng yêu cầu 2026-10-08).
   *
   * Quyền riêng, không suy từ `canSchedule` hay `canEditTasks`: đó là đúng quyền
   * mà `PATCH /tasks/:id/assignee` hỏi, và cũng là quyền mở ô Phụ trách ở bảng
   * Danh sách. Suy từ quyền khác thì có ngày nút hiện ra cho người luôn nhận 403.
   */
  readonly canAssign?: boolean;
  /**
   * Kỳ của dự án bảo trì — APM-MAINT-h.
   *
   * ⚠️ `Board` **tự gọi** truy vấn board của nó, không dùng lại truy vấn mà
   * `TasksPanel` đã có. Nên thiếu prop này thì bộ chọn kỳ đổi được URL, đổi được
   * Gantt và Danh sách, mà board vẫn hiện **mọi** việc — không lỗi, chỉ là một
   * tab không nghe lời.
   */
  readonly period?: string | null;
  /**
   * Bộ lọc/làm nổi dùng chung cho cả ba cách nhìn — APM-BOARD-h.
   *
   * Thanh điều khiển nay ở `TasksPanel`, vì đổi cách nhìn mà mất bộ lọc là
   * người dùng phải đặt lại ba lần cho cùng một câu hỏi.
   */
  readonly filter?: BoardFilter;
  /**
   * Task đang mở drawer, do **URL** quyết định — deep-link từ thông báo
   * (APM-NOTI-c). Bỏ trống thì board tự giữ trạng thái này như trước.
   */
  readonly openedTaskId?: string | null;
  readonly onOpenedTaskChange?: (taskId: string | null) => void;
}

/**
 * Board Kanban kéo thả — spec mục 1 và 13.
 *
 * Mọi thao tác là **optimistic**: thẻ nằm ở cột mới ngay khi thả tay, lỗi thì
 * hoàn tác kèm toast (docs/frontend-guide.md §3). Số học chỉ số nằm ở
 * `reorder.ts` — hàm thuần, có test riêng.
 */
export function Board(props: BoardProps): ReactNode {
  // Dấu MR + chấm CI trên thẻ (APM-GIT-i, §8.2): một lời gọi cho cả bảng, thẻ đọc qua context.
  return (
    <GitTaskMarksProvider projectId={props.projectId}>
      {/* Nhãn "AI rà commit thấy thấp" trên thẻ (APM-PCRV) — cùng khuôn một lời gọi cho cả bảng. */}
      <ProgressReviewWarningsProvider projectId={props.projectId}>
        <BoardBody {...props} />
      </ProgressReviewWarningsProvider>
    </GitTaskMarksProvider>
  );
}

function BoardBody({
  projectId,
  sprintId,
  all = false,
  canEditColumns = false,
  canManageColumns = false,
  canEditTasks = false,
  canSchedule = false,
  canAssign = false,
  period = null,
  filter = NO_BOARD_FILTER,
  openedTaskId,
  onOpenedTaskChange,
}: BoardProps): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  /**
   * Chỗ thẻ sẽ rơi vào, và chiều cao của thẻ đang nhấc — APM-BOARD-perf3.
   *
   * Chỉ hai con số, không phải cả một bản board dựng lại: cột nào, chỗ thứ mấy.
   * Cột ấy chèn thêm **một khung trống** ở đúng chỗ đó (xem `BoardColumn`), và
   * đó là toàn bộ phản hồi hình ảnh của cú kéo.
   *
   * ⚠️ Khung trống phải nằm ở **cột đích**, không phải cột xuất phát. Bản trước
   * để thẻ đang nhấc tự biến thành khung trống tại chỗ cũ của nó — kéo trong
   * cùng một cột thì trông vẫn đúng (dnd-kit dịch nó theo), nhưng kéo **sang
   * cột khác** thì cột đích không có gì cả, và người kéo không biết mình sắp
   * thả vào đâu.
   */
  const [dropAt, setDropAt] = useState<{ statusId: string; slotIndex: number } | null>(null);
  /**
   * Đo **một lần** lúc nhấc, từ số đo của chính dnd-kit.
   *
   * `null` ⇒ khung trống rơi về chiều cao đỡ. Đọc chiều cao ở mỗi bước kéo là
   * đọc một node đã bị thay bằng khung trống rồi, tức đo chính cái đỡ ấy.
   */
  const [dragHeight, setDragHeight] = useState<number | null>(null);

  /**
   * Thẻ đang chạy hiệu ứng đáp xuống — APM-BOARD-land.
   *
   * `move.onMutate` dời thẻ ngay khi nhả tay, nên nó hiện ra ở kích thước cuối
   * cùng trong đúng một khung hình: mắt không nối được "khung trống tôi vừa
   * nhắm" với "thẻ vừa hiện". `.board-land` cho nó hiện rõ dần và nở ra từ cỡ
   * khung trống, còn tập này nói thẻ **nào** đang chạy hiệu ứng ấy.
   *
   * ⚠️ Một **tập**, vì hiệu ứng dài giây rưỡi và sắp board là kéo liên tiếp —
   * xem docblock của `BoardColumn.landedIds`.
   */
  const [landedIds, setLandedIds] = useState<ReadonlySet<string>>(() => new Set());

  /**
   * Hẹn giờ gỡ, **một cái cho mỗi thẻ**.
   *
   * Không đợi `animationend`: `prefers-reduced-motion` đặt `animation: none`,
   * và khi ấy sự kiện ấy **không bao giờ bắn** — id sẽ nằm lại mãi và lần thả
   * sau của cùng thẻ không chạy lại được hiệu ứng (`className` không đổi nên
   * trình duyệt không khởi động lại animation).
   */
  const landTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const land = useCallback((taskId: string) => {
    setLandedIds((previous) => {
      if (previous.has(taskId)) return previous;
      return new Set(previous).add(taskId);
    });

    // Thả lại cùng một thẻ trong lúc nó còn đang nở: hẹn giờ cũ phải bỏ đi,
    // nếu không nó sẽ gỡ lớp giữa chừng lượt chạy mới.
    clearTimeout(landTimers.current.get(taskId));
    landTimers.current.set(
      taskId,
      setTimeout(() => {
        landTimers.current.delete(taskId);
        setLandedIds((previous) => {
          const next = new Set(previous);
          next.delete(taskId);
          return next;
        });
      }, LAND_MS),
    );
  }, []);

  useEffect(() => {
    const timers = landTimers.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  /*
    Board **không** dời thẻ trong lúc kéo — bỏ ở APM-BOARD-perf2, 2026-09-23.

    Trước đây có một state `preview`: `onDragOver` tính chỗ rơi rồi `applyMove`
    dựng lại cả board ở **mỗi bước kéo**, để người kéo thấy đúng chỗ thẻ sẽ vào.
    Nó trả ba cái giá, và cái thứ ba là cái giết nó:

    1. Mỗi bước kéo dựng lại mọi cột và mọi thẻ — đo được 26 ms cho 60 thẻ.
    2. Danh sách đổi thứ tự liên tục ⇒ `disableTransforms` của dnd-kit bật, nên
       chính hoạt hình "dẹp chỗ" có sẵn của nó bị tắt; ta tự làm lại bằng React
       một việc mà trình duyệt làm bằng CSS transform.
    3. Chỉ số của thẻ đổi liên tục ⇒ `useDerivedTransform` của dnd-kit
       `setState` trong một layout effect ở **mỗi** thẻ, mỗi bước — và React
       dừng hẳn với *"Maximum update depth exceeded"* ngay khi bắt đầu kéo.

    Nay: thẻ đứng yên, chỗ nó vừa rời thành một **khung trống đúng bằng nó**
    (xem `TaskCardBody`), còn việc "dẹp chỗ" để dnd-kit lo bằng transform. Chỗ
    rơi được tính **một lần** lúc nhả tay, từ `event.over`.
  */
  /** Cột xuất phát, chốt lúc bắt đầu kéo — sau đó board đã là bản xem trước. */
  const [origin, setOrigin] = useState<{ statusId: string; index: number } | null>(null);
  /**
   * Có `openedTaskId` thì URL là nguồn sự thật, không thì dùng state nội bộ.
   *
   * Giữ cả hai đường vì `Board` còn được dùng ở chỗ không có search param để
   * gắn vào; ép mọi nơi phải nối URL chỉ để mở một drawer là bắt chúng dựng
   * một router chỉ để hiện một cái thẻ.
   */
  const [ownOpenedId, setOwnOpenedId] = useState<string | null>(null);
  const controlled = openedTaskId !== undefined;
  const openedId = controlled ? openedTaskId : ownOpenedId;
  /**
   * ⚠️ `useCallback`, không phải một hàm viết thẳng — APM-BOARD-perf.
   *
   * Hàm này đi xuống tận `ColumnCard` làm prop. Dựng lại ở mỗi lần vẽ thì
   * `memo` của hàng thẻ không chặn được gì, và cả sáu mươi thẻ vẽ lại theo mỗi
   * pixel con trỏ nhích trong lúc kéo.
   */
  const setOpenedId = useCallback(
    (taskId: string | null) => {
      if (controlled) onOpenedTaskChange?.(taskId);
      else setOwnOpenedId(taskId);
    },
    [controlled, onOpenedTaskChange],
  );
  /**
   * Form tạo/sửa task — APM-1308b. `null` = đóng.
   *
   * `statusId` nhớ cột người dùng bấm "+": tạo từ cột "Đang làm" thì thẻ phải
   * rơi vào đúng cột đó, không phải cột mặc định của dự án.
   */
  const [form, setForm] = useState<{ statusId: string | null; taskId: string | null } | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  /**
   * Lọc/làm nổi thẻ — APM-BOARD-f. State cục bộ, không nằm ở URL.
   *
   * `Board` được dùng ở cả chỗ không có search param để gắn vào (xem
   * `openedTaskId`), nên ép nó phải có URL là bắt mọi nơi dựng một router chỉ
   * để nhớ một bộ lọc. Đổi lại: link chia sẻ chưa mang theo bộ lọc — nối vào
   * `ProjectSearch` là việc một buổi, khi nào cần chia sẻ thì làm.
   *
   * ⚠️ State đã **chuyển lên `TasksPanel`** ở APM-BOARD-h: cùng một bộ lọc nay
   * dùng chung cho cả ba cách nhìn, nên nó phải sống ở tổ tiên chung. Giữ mặc
   * định ở đây để `Board` vẫn dựng được một mình (test, và mọi chỗ chưa truyền).
   */
  const { user } = useAuth();

  const windowFilter = {
    mode: 'board' as const,
    limit: 100,
    today: todayIso(),
    ...(filter.mode === 'filter' && filter.assigneeId ? { assigneeId: filter.assigneeId } : {}),
    ...(filter.mode === 'filter' && filter.taskTypeId ? { taskTypeId: filter.taskTypeId } : {}),
    ...(filter.mode === 'filter' && filter.due ? { due: filter.due } : {}),
  };
  const windowScope = JSON.stringify([projectId, sprintId, all, period, windowFilter]);
  const [columnPages, setColumnPages] = useState<{
    scope: string;
    offsets: Record<string, number>;
  }>({ scope: '', offsets: {} });
  const offsets = columnPages.scope === windowScope ? columnPages.offsets : {};
  const key = [...boardKeys.board(projectId, sprintId, period), 'window', windowFilter, offsets];
  const board = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      boardApi.window(
        projectId,
        sprintId,
        all,
        period,
        {
          ...windowFilter,
          offsets: Object.fromEntries(Object.entries(offsets).filter(([id]) => id !== BACKLOG_ID)),
        },
        signal,
      ),
  });

  /**
   * Dự phòng cho deep-link: task được trỏ tới **không nằm trong board đang xem**.
   *
   * Board của dự án Agile chỉ chứa sprint đang chạy cộng backlog, nên một thông
   * báo về task ở sprint đã đóng sẽ mở đúng dự án rồi... không mở gì cả. Một
   * link im lặng không làm gì là thứ người dùng không báo lỗi được — họ chỉ
   * nghĩ mình bấm hụt.
   *
   * `enabled` chỉ bật khi board đã tải xong mà vẫn không tìm thấy: trường hợp
   * thường gặp (task có trên board) không tốn thêm request nào.
   */
  const missingFromBoard =
    openedId !== null && board.data !== undefined && findTask(board.data, openedId) === null;

  const openedFallback = useQuery({
    queryKey: boardKeys.task(openedId ?? ''),
    queryFn: () => boardApi.task(openedId!),
    enabled: missingFromBoard,
    // Task đã bị xoá là một kết cục hợp lệ của deep-link, không phải sự cố mạng.
    retry: false,
  });

  /**
   * Hàng đợi chưa vào sprint — cột đầu tiên của board.
   *
   * Chỉ tải ở board theo sprint. Ở board "tất cả" (Waterfall) thì không có
   * sprint nào, nên mọi task đều "chưa vào sprint" và một cột Backlog ở đó sẽ
   * nhân đôi toàn bộ board.
   */
  const backlogKey = [
    ...boardKeys.backlog(projectId, period),
    'window',
    windowFilter,
    offsets[BACKLOG_ID] ?? 0,
  ];
  const backlog = useQuery({
    queryKey: backlogKey,
    queryFn: ({ signal }) =>
      boardApi.backlog(
        projectId,
        period,
        false,
        { ...windowFilter, offset: offsets[BACKLOG_ID] ?? 0 },
        signal,
      ),
    enabled: !all,
  });

  const sensors = useSensors(
    // Ngưỡng 6px: không có nó thì mỗi cú click vào thẻ đều bị hiểu là bắt đầu
    // kéo, và các nút bên trong thẻ (preset tiến độ) không bấm được.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    /*
      Bộ toạ độ **mặc định** của dnd-kit — APM-BOARD-perf4.

      `sortableKeyboardCoordinates` đọc ngữ cảnh của `SortableContext`, thứ
      board này không còn dùng (xem docblock của `TaskCard`). Mặc định nhích
      theo bước 25px thay vì nhảy sang thẻ kế; thô hơn, nhưng `boardCollision`
      vẫn tra đúng chỗ rơi cho nó — xem nhánh không có `pointerCoordinates`.
    */
    useSensor(KeyboardSensor),
  );

  /*
    Hoàn thành một việc qua hai lời hỏi: thời gian tối thiểu của dự án (APM-TMIN-b)
    và đồng hồ bấm giờ đang chạy (APM-TIME-done). Một hộp cho cả board.
  */
  const complete = useCompleteTaskGuard();

  const move = useMutation<MoveResult, unknown, MoveVariables, { previous: BoardView | undefined }>(
    {
      mutationFn: async ({ taskId, statusId, index, serverIndex, sameColumn, actualStartAt }) => {
        // Hai endpoint khác nhau cho hai thao tác khác nhau: đổi cột là sự kiện
        // nghiệp vụ (`task.status_changed`), còn đổi thứ tự trong cột thì không.
        if (sameColumn) {
          await boardApi.reorder(taskId, serverIndex ?? index);
          return { warnings: [] };
        }
        const result = await boardApi.changeStatus(
          taskId,
          statusId,
          serverIndex ?? index,
          actualStartAt,
        );
        return { warnings: result.warnings };
      },

      onMutate: async (variables) => {
        /*
          Chỉ khi **đổi cột**. Sắp lại thứ tự trong cùng một cột thì thẻ không
          đáp xuống đâu cả — nó trượt vài chục pixel, và cho nó nở ra ở đó là
          một chuyển động không nói lên điều gì.
        */
        if (!variables.sameColumn) land(variables.taskId);

        await queryClient.cancelQueries({ queryKey: key });
        const previous = queryClient.getQueryData<BoardView>(key);
        if (previous) {
          queryClient.setQueryData(
            key,
            applyMove(previous, variables.taskId, variables.statusId, variables.index),
          );
        }
        return { previous };
      },

      onError: (error, variables, context) => {
        // Hoàn tác về đúng ảnh chụp trước thao tác, không phải `invalidate`:
        // invalidate để thẻ nằm sai chỗ cho tới khi request mới về.
        if (context?.previous) queryClient.setQueryData(key, context.previous);
        // Chưa đủ thời gian tối thiểu (APM-TMIN-b) ⇒ hộp khai giờ, khai xong thì thả lại.
        const task = context?.previous ? findTask(context.previous, variables.taskId) : null;
        if (
          complete.handleError(
            error,
            task ?? { title: '' },
            (actualStartAt) => moveTask({ ...variables, actualStartAt }),
            () => moveTask(variables),
          )
        ) {
          return;
        }
        toast.error(errorMessage(error, t));
      },

      onSuccess: (result) => {
        for (const warning of result.warnings) {
          // WIP limit là cảnh báo, không phải lỗi — thao tác đã thành công.
          toast.warning(warning.message);
        }
      },

      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: key });
      },
    },
  );

  /**
   * Đổi sprint của một lô việc.
   *
   * **Không** optimistic như `move`: thao tác này đổi cả hai danh sách (board
   * và backlog) và có thể chạm nhiều task một lượt. Dựng ảnh chụp lạc quan cho
   * cả hai rồi hoàn tác đúng khi lỗi là nhiều mã hơn giá trị nó mua — chờ
   * server rồi nạp lại là đủ nhanh cho một thao tác theo lô.
   */
  const toSprint = useMutation({
    mutationFn: async ({
      taskIds,
      sprintId: target,
      position,
    }: {
      taskIds: string[];
      sprintId: string | null;
      /** Chỉ có khi kéo **một** thẻ; thao tác theo lô luôn xếp cuối. */
      position?: number;
      /**
       * Thẻ chạy hiệu ứng đáp xuống sau khi dữ liệu về — APM-BOARD-land.
       *
       * Chỉ cú **kéo** đặt trường này. Chuyển lô bằng nút thì không có thẻ nào
       * "vừa đáp": người dùng đang nhìn thanh hành động chứ không nhìn cột, và
       * mười thẻ cùng nở ra một lúc là nhiễu, không phải phản hồi.
       */
      land?: string;
    }) => {
      // Tuần tự, không `Promise.all`: mỗi lần chuyển là một command ghi event,
      // và mười request song song vào cùng một dự án chỉ để giành nhau khoá.
      for (const taskId of taskIds) {
        await boardApi.moveToSprint(taskId, target, position);
      }
    },
    onError: (error) => toast.error(errorMessage(error, t)),
    onSuccess: (_result, variables) => {
      setSelectedIds(new Set());
      toast.success(
        variables.sprintId === null
          ? t('board.movedToBacklog', { count: variables.taskIds.length })
          : t('board.movedToSprint', { count: variables.taskIds.length }),
      );
    },
    onSettled: async (_data, _error, variables) => {
      // Danh sách sprint đi kèm phản hồi backlog, nên hai query này là đủ.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        queryClient.invalidateQueries({ queryKey: backlogKey }),
      ]);

      /*
        Sau `await`, không trước — APM-BOARD-land. Đường này **không** lạc quan:
        thẻ chỉ xuất hiện ở cột đích khi dữ liệu mới về. Đặt id lúc nhả tay thì
        `LAND_MS` đếm xong trước cả lúc có thẻ để chạy hiệu ứng, và cú kéo vào
        Backlog lặng lẽ không có phản hồi nào.
      */
      if (variables.land !== undefined) land(variables.land);
    },
  });

  /** Tạo sprint mới rồi đưa lô đang chọn vào đó — một thao tác của người dùng. */
  const createSprintAndMove = useMutation({
    mutationFn: async ({
      name,
      color,
      taskIds,
    }: {
      name: string;
      color: string | null;
      taskIds: string[];
    }) => {
      const sprint = await boardApi.createSprint(projectId, {
        name,
        ...defaultSprintRange(),
        ...(color === null ? {} : { color }),
      });
      for (const taskId of taskIds) {
        await boardApi.moveToSprint(taskId, sprint.id);
      }
    },
    onError: (error) => toast.error(errorMessage(error, t)),
    onSuccess: (_result, variables) => {
      setSelectedIds(new Set());
      toast.success(t('board.movedToSprint', { count: variables.taskIds.length }));
    },
    onSettled: async () => {
      // Danh sách sprint đi kèm phản hồi backlog, nên hai query này là đủ.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        queryClient.invalidateQueries({ queryKey: backlogKey }),
      ]);
    },
  });

  /**
   * Sửa cột board. Mọi endpoint cột trả về **danh sách cột sau khi sửa**, nhưng
   * board vẫn phải tải lại: đổi nhóm của một cột đổi luôn cách server tính
   * `overWipLimit` và thứ tự thẻ.
   */
  const columns = useMutation({
    // Trả `void`: kết quả bốn nhánh khác kiểu nhau (ba trả danh sách cột, một
    // trả 204), và chỗ này nạp lại từ query chứ không dùng giá trị trả về.
    mutationFn: async (action: ColumnAction): Promise<void> => {
      switch (action.kind) {
        case 'create':
          await boardApi.createStatus(projectId, action.input);
          return;
        case 'update':
          await boardApi.updateStatus(projectId, action.statusId, action.input);
          return;
        case 'reorder':
          await boardApi.reorderStatuses(projectId, action.statusIds);
          return;
        case 'delete':
          await boardApi.deleteStatus(projectId, action.statusId);
          return;
      }
    },
    onError: (error) => toast.error(errorMessage(error, t)),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        // Danh sách cột nằm trong `ProjectView`, mà bộ chọn dự án và Gantt đều
        // đọc nó.
        queryClient.invalidateQueries({ queryKey: boardKeys.projects() }),
      ]);
    },
  });

  /**
   * Kéo cột vào khung sprint hoặc ra ngoài khung — APM-BOARD-b.
   *
   * ⚠️ Đây là đổi **chủ sở hữu** cột. Task nào đang ở trong cột mà không thuộc
   * sprint mới sẽ bị dời sang cột khác trên board của chính nó, nên phải hỏi
   * lại khi cột không rỗng — và báo lại con số thật server trả về, không phải
   * con số client đoán trước.
   */
  const moveColumn = useMutation({
    mutationFn: ({ statusId, sprintId: target }: { statusId: string; sprintId: string | null }) =>
      boardApi.moveStatus(projectId, statusId, target),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSuccess: (result) => {
      if (result.displacedTaskCount > 0) {
        toast.warning(t('board.columnMovedDisplaced', { count: result.displacedTaskCount }));
      } else {
        toast.success(t('board.columnMoved'));
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        queryClient.invalidateQueries({ queryKey: backlogKey }),
        queryClient.invalidateQueries({ queryKey: boardKeys.projects() }),
      ]);
    },
  });

  /**
   * Hỏi lại trước khi đổi chủ sở hữu một cột **không rỗng**.
   *
   * `confirm` chứ không phải hộp thoại riêng: đây là thao tác hiếm và nguy
   * hiểm, và điều duy nhất quan trọng là người dùng dừng lại một nhịp trước khi
   * mười việc đổi cột.
   */
  function askMoveColumn(column: BoardColumnData, target: string | null): void {
    if (column.count > 0) {
      const question = t('board.confirmMoveColumn', {
        name: column.status.name,
        count: column.count,
      });
      if (!window.confirm(question)) return;
    }
    moveColumn.mutate({ statusId: column.status.id, sprintId: target });
  }

  /*
    Việc đang bấm giờ mà thành xong (100%, kéo sang cột Hoàn thành) thì server
    dừng đồng hồ — hỏi trước (APM-TIME-done). Một hộp cho cả board.
  */
  // Và việc chưa đủ thời gian tối thiểu của dự án thì hỏi khai giờ bắt đầu — APM-TMIN-b.
  const viewRef = useRef<BoardView | null>(null);
  const completeGuard = complete.guard;
  const guardStop = useCallback(
    (
      taskId: string,
      completes: boolean,
      run: (actualStartAt?: string) => void,
      onCancel?: () => void,
      enters: boolean = completes,
    ) => {
      const task = viewRef.current ? findTask(viewRef.current, taskId) : null;
      completeGuard(task ?? { id: taskId, title: '' }, completes, run, onCancel, enters);
    },
    [completeGuard],
  );

  const progress = useMutation({
    mutationFn: ({
      taskId,
      progressPct,
      actualStartAt,
    }: {
      taskId: string;
      progressPct: number;
      actualStartAt?: string;
    }) => boardApi.setProgress(taskId, progressPct, actualStartAt),
    onError: (error, variables) => {
      const task = viewRef.current ? findTask(viewRef.current, variables.taskId) : null;
      if (
        complete.handleError(
          error,
          task ?? { title: '' },
          (actualStartAt) => progressMutate({ ...variables, actualStartAt }),
          () => progressMutate(variables),
        )
      ) {
        return;
      }
      toast.error(errorMessage(error, t));
    },
    onSuccess: (_result, variables) => {
      if (variables.progressPct === 100) offerToFinish(variables.taskId);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  /**
   * Đổi độ ưu tiên ngay trên thẻ — APM-BOARD-k.
   *
   * Dùng **đúng** đường mà bảng Danh sách đang dùng (`PATCH /tasks/:id`), không
   * mở endpoint riêng: hai màn hình đổi cùng một trường mà đi hai đường thì
   * chúng sẽ lệch nhau ở đúng chỗ khó thấy — cửa quyền và danh sách cảnh báo.
   */
  const setPriority = useMutation({
    mutationFn: ({ taskId, priority }: { taskId: string; priority: TaskPriority }) =>
      taskFormApi.update(taskId, { priority }),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSuccess: () => toast.success(t('board.priorityChanged')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  /*
    Ba callback đi xuống từng thẻ, ghim lại một lần — APM-BOARD-perf.

    `mutate` của React Query ổn định qua các lần vẽ, nên ba hàm này cũng ổn
    định, và `memo` của `ColumnCard` mới chặn được. Viết thẳng chúng trong
    `renderColumn` (như trước) thì mỗi lần board vẽ lại là một bộ hàm mới cho
    **mọi** thẻ — đo được 60/60 thẻ vẽ lại cho mỗi lần board vẽ lại.
  */
  const progressMutate = progress.mutate;
  const handleProgress = useCallback(
    (taskId: string, progressPct: number) =>
      guardStop(taskId, progressPct >= 100, (actualStartAt) =>
        progressMutate({ taskId, progressPct, ...(actualStartAt ? { actualStartAt } : {}) }),
      ),
    [guardStop, progressMutate],
  );

  /** Đổi hạn ngay trên thẻ — cùng đường `PATCH /tasks/:id` với bảng Danh sách. */
  const setDue = useMutation({
    mutationFn: ({ taskId, dueDate }: { taskId: string; dueDate: string }) =>
      taskFormApi.update(taskId, { dueDate }),
    onError: (error) => toast.error(errorMessage(error, t)),
    onSuccess: () => toast.success(t('board.dueChanged')),
    // Hạn đổi thì cả Gantt và dải "việc đang chậm deadline" cũng đổi theo.
    onSettled: () => queryClient.invalidateQueries(),
  });
  const dueMutate = setDue.mutate;
  const handleDueDate = useCallback(
    (taskId: string, dueDate: string) => dueMutate({ taskId, dueDate }),
    [dueMutate],
  );

  const priorityMutate = setPriority.mutate;
  const handlePriority = useCallback(
    (taskId: string, priority: TaskPriority) => priorityMutate({ taskId, priority }),
    [priorityMutate],
  );

  /**
   * Thành viên dự án cho danh sách chọn người trên thẻ.
   *
   * Cùng khoá cache với form sửa việc và bảng Danh sách, nên đổi qua lại giữa
   * ba chỗ không tải lại. Chỉ nạp khi người xem giao được việc — người khác
   * không có nút nào để mở danh sách này.
   */
  const members = useQuery({
    queryKey: taskFormKeys.members(projectId),
    queryFn: () => taskFormApi.members(projectId),
    staleTime: 5 * 60_000,
    enabled: canAssign,
  });

  /**
   * Giao lại việc ngay trên thẻ — đường riêng `PATCH /tasks/:id/assignee` như
   * form và bảng Danh sách, vì nó trả cảnh báo quá tải / vắng mặt phải hiện ra.
   */
  const assign = useMutation({
    mutationFn: ({ taskId, assigneeId }: { taskId: string; assigneeId: string | null }) =>
      taskFormApi.assign(taskId, assigneeId),
    onSuccess: (result) => {
      for (const warning of result.warnings) toast.warning(warning.message);
      toast.success(t('taskForm.assigned'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
    // Đổi người làm thì tải của cả hai người, "Việc của tôi" và bảng Danh sách
    // cũng đổi theo — không chỉ board này.
    onSettled: () => queryClient.invalidateQueries(),
  });
  const assignMutate = assign.mutate;
  const handleAssign = useCallback(
    (taskId: string, assigneeId: string | null) => assignMutate({ taskId, assigneeId }),
    [assignMutate],
  );
  const memberList = members.data;

  const handleSelect = useCallback((taskId: string, selected: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (selected) next.add(taskId);
      else next.delete(taskId);
      return next;
    });
  }, []);

  /**
   * 100% tiến độ ⇒ **mời** chuyển sang cột Hoàn thành, không tự chuyển.
   *
   * Hai thứ khác nhau: "đã làm hết phần việc" và "đã xong theo định nghĩa của
   * đội" (có đội đòi review, có đội đòi QA — đó là lý do cột board tuỳ biến
   * được). Tự chuyển thì hệ thống tự kết luận thay người dùng, và
   * `task.status_changed` sẽ mang tên họ cho một quyết định họ không đưa ra.
   *
   * Nhưng để im thì 100% nằm mãi ở cột "đang làm" — đúng cái làm burndown và
   * cycle time nói sai. Nên: hỏi một câu, họ bấm hoặc bỏ qua.
   *
   * Toast có nút chứ không phải hộp thoại chặn: người dùng đang kéo tiến độ
   * của nhiều thẻ liền nhau, và một modal ở giữa dòng thao tác đó là một cú
   * dừng cho mỗi thẻ.
   */
  function offerToFinish(taskId: string): void {
    const data = queryClient.getQueryData<BoardView>(key);
    const done = data?.columns.find((column) => column.status.category === 'done');
    if (!done) return;

    const task = data ? findTask(data, taskId) : null;
    if (!task || task.statusCategory === 'done') return;

    toast(t('board.finishPrompt', { title: task.title }), {
      duration: 10_000,
      action: {
        label: t('board.finishConfirm', { column: done.status.name }),
        onClick: () =>
          guardStop(taskId, true, (actualStartAt) =>
            moveTask({
              taskId,
              statusId: done.status.id,
              index: done.tasks.length,
              sameColumn: false,
              ...(actualStartAt ? { actualStartAt } : {}),
            }),
          ),
      },
    });
  }

  if (board.isPending) {
    return (
      <div className="flex gap-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-64 w-72" />
        ))}
      </div>
    );
  }

  if (board.isError) {
    return (
      <ErrorState message={errorMessage(board.error, t)} onRetry={() => void board.refetch()} />
    );
  }

  const data = board.data;
  // Giữ *id* chứ không giữ nguyên object task: sau khi bình luận làm board
  // refetch, một object đã chụp lại sẽ giữ trạng thái cũ trong drawer.
  const opened = (openedId ? findTask(data, openedId) : null) ?? openedFallback.data ?? null;

  /**
   * Cột Backlog **ảo**: tasks lấy từ `/backlog`, không từ một `workflow_status`.
   *
   * Dựng thành `BoardColumnData` để mọi thứ còn lại — `dropIndex`, `columnOf`,
   * `BoardColumn` — không phải biết Backlog là trường hợp riêng. WIP limit là
   * `null`: giới hạn việc-đang-làm không áp cho một hàng đợi.
   */
  const backlogTasks = all ? [] : (backlog.data?.tasks ?? []);
  const backlogColumn: BoardColumnData | null = all
    ? null
    : {
        status: {
          id: BACKLOG_ID,
          name: t('board.backlog'),
          slug: BACKLOG_ID,
          category: 'todo',
          position: -1,
          color: null,
          wipLimit: null,
          isDefault: false,
          // Backlog nằm **ngoài** mọi sprint theo đúng nghĩa của nó — cột ảo
          // này chỉ đang nói ra điều đó bằng cùng một trường như cột thật.
          sprintId: null,
        },
        tasks: backlogTasks,
        ...(backlog.data?.page ? { page: backlog.data.page } : {}),
        count: backlog.data?.page?.fullTotal ?? backlogTasks.length,
        overWipLimit: false,
      };

  /** Board + Backlog, **chưa** tính nước đi đang kéo. */
  const baseView: BoardView = mergeBacklogColumn(data, backlogColumn);

  /**
   * Board đang hiển thị — **không** còn nhánh xem trước (APM-BOARD-perf2).
   *
   * Giữ tên `view` thay vì thay hết bằng `baseView`: nó xuất hiện ở vài chục
   * chỗ bên dưới, và một lần đổi tên trải rộng như thế che mất thay đổi thật
   * trong lịch sử. `applyMove` vẫn là đường duy nhất dời thẻ, nay chỉ chạy ở
   * `move.onMutate` — tức đúng một lần, lúc thả tay.
   */
  const view: BoardView = baseView;
  viewRef.current = view;

  /**
   * Thẻ đang bay theo con trỏ. Tìm trên `view` chứ không trên `data`: Backlog
   * là cột **ảo**, task của nó không nằm trong `data.columns` — tìm ở đó thì
   * kéo một thẻ từ Backlog sẽ không có thẻ nào bay theo tay.
   */
  const dragging = draggingId ? findTask(view, draggingId) : null;

  const sprints = (backlog.data?.sprints ?? []).filter(
    (sprint) => sprint.status === 'active' || sprint.status === 'planning',
  );
  /** Mọi sprint cho bộ chọn — kể cả đã đóng, vì xem lại sprint cũ là chuyện thường. */
  const sprintOptions = backlog.data?.sprints ?? [];
  const selectedList = [...selectedIds];

  /**
   * Hai nhóm cột — APM-BOARD-a3 trả `status.sprintId`, đây là chỗ dùng nó.
   */
  const insideColumns = view.columns.filter((column) => column.status.sprintId !== null);
  /**
   * Cột chung **rỗng** không hiện trên board của một sprint — APM-BOARD-b.
   *
   * Dự án Agile có cả ba cột cấp dự án (`todo`/`in-progress`/`done`, chỗ đứng
   * cho việc chưa vào sprint — `task.status_id` là NOT NULL) lẫn ba cột riêng
   * của từng sprint. Vẽ cả sáu thì board có ba cặp trùng tên, và ba cột chung
   * còn đẩy khung sprint ra khỏi màn hình.
   *
   * Trên board của một sprint, việc chưa vào sprint đã nằm ở cột Backlog ngay
   * bên trái, nên cột chung rỗng không nói thêm điều gì.
   *
   * ⚠️ Chỉ ẩn khi **rỗng**, và chỉ khi đang xem một sprint. Một task đang ở
   * trong sprint mà mang trạng thái của cột chung là chuyện hợp lệ (cột ngoài
   * sprint dùng được ở mọi board), và ẩn cứng sẽ làm nó biến mất khỏi board mà
   * không có lỗi nào. Ẩn theo cách này thì mọi thứ có thẻ đều còn nhìn thấy.
   *
   * ⚠️ Backlog **luôn** hiện, kể cả khi rỗng: nó là chỗ để **thả** việc ra khỏi
   * sprint, và một vùng thả biến mất đúng lúc nó trống là thứ không dùng được.
   */
  const outsideColumns = view.columns.filter(
    (column) =>
      column.status.sprintId === null &&
      (column.status.id === BACKLOG_ID || insideColumns.length === 0 || column.count > 0),
  );

  /**
   * Hôm nay, chốt **một lần** cho cả lần vẽ này.
   *
   * Gọi `todayIso()` ở từng thẻ thì hai thẻ vẽ cách nhau đúng lúc nửa đêm sẽ
   * rơi vào hai ngăn thời hạn khác nhau — hiếm, nhưng là loại lỗi không ai
   * dựng lại được.
   */
  const today = todayIso();

  /** Thẻ bị làm chìm ở chế độ "làm nổi" — rỗng ở mọi chế độ khác. */
  const dimmed = dimmedIdsOf(view, filter, today);

  function moveTask(variables: MoveVariables): void {
    move.mutate({
      ...variables,
      serverIndex: persistedDropIndex(view, variables.taskId, variables.statusId, variables.index),
    });
  }

  function renderColumn(column: BoardColumnData): ReactNode {
    const isBacklog = column.status.id === BACKLOG_ID;

    /*
      ⚠️ Lọc **chỉ ở lớp vẽ** — `view` (thứ `dropIndex` đọc) vẫn nguyên vẹn.

      Người dùng thấy ba thẻ nhưng cột có mười; gửi chỉ số của danh sách đã lọc
      lên server là xếp thẻ vào sai chỗ trong bảy thẻ còn lại — sai một cách im
      lặng, vì trên màn hình đã lọc thì trông vẫn đúng. `dropIndex` tra `overId`
      trong danh sách đầy đủ nên nó vẫn ra đúng chỉ số thật.

      `count` cũng giữ nguyên số thật: badge WIP nói về cột, không nói về cái
      đang lọc. Một cột "2/3" tụt xuống "1/3" chỉ vì vừa lọc là một lời nói dối
      về trần việc-đang-làm.
    */
    const filtering = filter.mode === 'filter' && isFilterActive(filter);
    /*
      Mốc **không** có thẻ trên Kanban — người dùng chốt 2026-09-25. Mốc là một
      điểm trên trục thời gian, không "chảy" qua các cột; nó hiện ở dải "Mốc"
      phía trên board, trên Gantt và ở Danh sách. Ẩn theo đúng đường của bộ lọc
      ở trên, nên chỉ số kéo thả vẫn tính trên cột đầy đủ.
    */
    const visible = (task: TaskView): boolean =>
      task.kind !== 'milestone' && (!filtering || matchesFilter(task, filter, today));
    // Giữ nguyên object khi không ẩn gì — `BoardColumn` là memo.
    const hides = filtering || column.tasks.some((task) => task.kind === 'milestone');
    const shown = hides ? { ...column, tasks: column.tasks.filter(visible) } : column;

    /**
     * Chỉ số khung trống phải quy về **danh sách đang hiện** — APM-BOARD-perf3.
     *
     * `dropIndex` tính trên danh sách đầy đủ, và đó là đúng: chỉ số gửi lên
     * server phải là chỗ thật trong cột, không phải chỗ trong cái đang lọc (xem
     * chú thích ngay trên). Nhưng khung trống thì **vẽ** vào danh sách đã lọc,
     * nên nếu dùng thẳng chỉ số ấy, khung sẽ nhảy đi đâu đó — càng lọc mạnh
     * càng lệch xa.
     *
     * Đếm số thẻ **còn hiện** đứng trước chỗ chèn là ra đúng vị trí, và phép
     * đếm ấy đúng cả khi chính thẻ ở chỗ chèn đang bị lọc đi.
     */
    const slotIndex =
      dropAt?.statusId === column.status.id ? slotIndexIn(column, dropAt.slotIndex) : null;

    /**
     * Chỗ chen (danh sách đầy đủ) → chỗ vẽ (danh sách **đã lọc**).
     *
     * `slotIndexOf` đếm trên danh sách đầy đủ — phải thế, vì chỉ số gửi server
     * là chỗ thật trong cột. Nhưng khung trống vẽ vào danh sách đã lọc, nên
     * đếm lại số thẻ **còn hiện** đứng trước chỗ chen mới ra chỗ vẽ.
     */
    function slotIndexIn(full: BoardColumnData, slot: number): number {
      if (!hides) return slot;
      return full.tasks.slice(0, slot).filter(visible).length;
    }

    return (
      <BoardColumn
        key={column.status.id}
        column={shown}
        {...(column.page
          ? {
              onPage: (offset: number) =>
                setColumnPages({
                  scope: windowScope,
                  offsets: { ...offsets, [column.status.id]: offset },
                }),
            }
          : {})}
        draggableCards={canSchedule}
        // Backlog là chuyện sprint — lập kế hoạch, chỉ PM.
        statusDragCards={column.status.id !== BACKLOG_ID}
        dimmedIds={dimmed}
        landedIds={landedIds}
        {...(slotIndex === null ? {} : { dropIndicator: { index: slotIndex, height: dragHeight } })}
        onProgress={handleProgress}
        onOpen={setOpenedId}
        {
          /* Đổi ưu tiên là lập kế hoạch như đổi hạn: chỉ PM (`assertMaySchedule` ở API). */
          ...(canSchedule ? { onPriority: handlePriority } : {})
        }
        {
          /*
            Đổi hạn là lập kế hoạch: PM (`project:update`), hoặc người mà cài đặt "Quyền đổi
            ngày" của dự án cho phép trên từng việc (`canSchedule` — APM-TSCH-b). Thẻ nào hiện
            nút do `BoardCard` quyết theo cờ của chính thẻ ấy.
          */
          ...(canSchedule || column.tasks.some((task) => task.canSchedule === true)
            ? { onDueDate: handleDueDate }
            : {})
        }
        {
          /* Giao lại việc từ chân thẻ: `task:assign`. Chưa tải xong thành viên thì nút vẫn có,
             danh sách tự đầy khi dữ liệu về. */
          ...(canAssign
            ? { onAssign: handleAssign, ...(memberList ? { members: memberList } : {}) }
            : {})
        }
        {...(isBacklog
          ? {
              variant: 'backlog' as const,
              selectedIds,
              onSelect: handleSelect,
              ...(column.tasks.length > 0 ? { hint: t('board.backlogHint') } : {}),
            }
          : canManageColumns
            ? { menu: columnMenu(column) }
            : {})}
      />
    );
  }

  if (view.columns.every((column) => column.tasks.length === 0)) {
    return (
      <>
        <EmptyState
          title={t('board.emptyTitle')}
          description={
            /* Dự án bảo trì / Waterfall không có sprint: "ở sprint này" là nói sai chuyện. */
            period
              ? t('board.emptyDescriptionPeriod')
              : all
                ? t('board.emptyDescriptionAll')
                : t('board.emptyDescription')
          }
        />
        {/*
          Nút tạo phải có **ở đây nữa** — APM-1308b: board rỗng là đúng lúc
          người dùng cần tạo task nhất, và trước đó màn hình này là ngõ cụt.
        */}
        <div className="mt-3 flex justify-center">
          <Button onClick={() => setForm({ statusId: null, taskId: null })}>
            {t('taskForm.newTask')}
          </Button>
        </div>
        {form !== null && (
          <TaskFormDialog
            projectId={projectId}
            statusId={form.statusId}
            canApprove={canEditColumns}
            onClose={() => setForm(null)}
          />
        )}
      </>
    );
  }

  function onDragStart(event: DragStartEvent): void {
    const activeId = String(event.active.id);
    setDraggingId(activeId);
    setDragHeight(event.active.rect.current.initial?.height ?? null);

    const statusId = columnOf(baseView, activeId);
    const column = baseView.columns.find((entry) => entry.status.id === statusId);
    const at =
      statusId === null || !column
        ? null
        : { statusId, index: column.tasks.findIndex((task) => task.id === activeId) };
    setOrigin(at);
    // Khung trống bắt đầu ngay tại chỗ thẻ vừa rời: nhấc lên mà cột thụt lại
    // một nhịp rồi mới có khung là một cú giật ngay ở đầu thao tác.
    setDropAt(at ? { statusId: at.statusId, slotIndex: at.index } : null);
  }

  /**
   * Dời **khung trống**, không dời thẻ — APM-BOARD-perf3.
   *
   * Đây là `setState` duy nhất trong cả cú kéo, và nó chỉ chạy khi chỗ rơi đổi
   * thật (không phải mỗi pixel). Bản trước dựng lại cả board ở đây bằng
   * `applyMove`, và chính việc thứ tự danh sách đổi liên tục đã đẩy dnd-kit vào
   * vòng lặp `setState` — xem chú thích ở đầu component.
   */
  function onDragOver(event: DragOverEvent): void {
    const activeId = String(event.active.id);
    const target = targetOf(activeId, event.over ? String(event.over.id) : null);

    setDropAt((current) =>
      current &&
      target &&
      current.statusId === target.statusId &&
      current.slotIndex === target.slotIndex
        ? current
        : target,
    );
  }

  /**
   * Chỗ chen vào cho một cặp (thẻ đang kéo, thứ đang đè lên).
   *
   * ⚠️ Con trỏ ra **ngoài mọi cột** ⇒ trả về chỗ xuất phát, không phải giữ
   * nguyên chỗ cuối cùng. Kéo ra một vị trí không tồn tại rồi thả thì thẻ phải
   * về chỗ cũ, nên khung trống cũng phải nói trước điều đó — giữ nguyên chỗ cũ
   * là hứa một nước đi mà cú thả sẽ không thực hiện.
   */
  function targetOf(
    activeId: string,
    overId: string | null,
  ): { statusId: string; slotIndex: number } | null {
    const home = origin ? { statusId: origin.statusId, slotIndex: origin.index } : null;
    if (overId === null || overId === activeId) return home;

    return (
      slotIndexOf(
        baseView,
        overId,
        baseView.columns.some((column) => column.status.id === overId),
      ) ?? home
    );
  }

  function onDragEnd(event: DragEndEvent): void {
    const activeId = String(event.active.id);
    const from = origin;
    setDraggingId(null);
    setOrigin(null);
    setDropAt(null);
    setDragHeight(null);

    /**
     * Chỗ rơi tính **một lần, tại đây** — APM-BOARD-perf2.
     *
     * Trước đây nó là bản xem trước đã tính sẵn ở `onDragOver`, và chú thích cũ
     * nói rằng tính lại lúc thả là mở đường cho "nhìn một đằng rơi một nẻo".
     * Lý lẽ ấy đúng **khi** board tự dời thẻ trong lúc kéo: lúc đó cái nhìn
     * thấy là một nước đi đã chốt, nên phải chốt đúng nước ấy.
     *
     * Nay board không dời thẻ nữa (xem chú thích ở đầu component), nên thứ
     * người dùng nhìn thấy là **vùng thả đang sáng** và khoảng trống do dnd-kit
     * dẹp ra — cả hai đều đến từ chính `event.over` này. Tính ở đây là tính từ
     * đúng nguồn ấy, không còn hai đường để lệch nhau.
     */
    const slot = targetOf(activeId, event.over ? String(event.over.id) : null);
    const target = slot
      ? { statusId: slot.statusId, index: toRemovalIndex(slot.slotIndex, slot.statusId, from) }
      : null;

    /** Không có đích, hoặc đích **chính là chỗ cũ** ⇒ không có gì để gửi. */
    if (!target || !from || (from.statusId === target.statusId && from.index === target.index)) {
      return;
    }

    /*
      Cửa "bắt đầu làm" — APM-BOARD-g, nói **trước khi** gửi.

      Server vẫn là chỗ chặn thật (`checkCanStart`), nhưng để nó trả lời thì
      thẻ đã kịp nhảy sang cột mới rồi bật ngược lại khi lỗi về — người dùng
      thấy một cú giật rồi mới thấy toast. Hai điều kiện board nhìn thấy được
      thì trả lời ngay tại đây; điều kiện thứ ba (phụ thuộc) vẫn để server.
    */
    const movingFrom = view.columns.find((column) => column.status.id === from.statusId);
    const movingTo = view.columns.find((column) => column.status.id === target.statusId);
    /*
      Cú thả này có đưa việc **vào việc** ("đang làm" / "xong") không — APM-EREF-c:
      việc thiếu hạn / ước lượng thì popup điền nhanh trước khi gửi. Từ Backlog thì
      cột nguồn không nằm trong `view.columns`; đọc nhóm trạng thái của chính thẻ.
    */
    const enters = entersWork(
      movingFrom?.status.category ?? findTask(view, activeId)?.statusCategory ?? 'todo',
      movingTo?.status.category ?? '',
    );

    /*
      Người không lập kế hoạch chỉ kéo để **đổi cột** — APM-TSP-d. Đổi thứ tự
      trong cột vẫn là việc của PM (APM-CHANGE-b). Kéo qua Backlog (đổi sprint)
      là của PM **hoặc người tạo việc** (APM-SPR-own, `canPlanSprint`): người
      khác thì thẻ về chỗ cũ và nói vì sao.
    */
    if (!canSchedule) {
      const crossesBacklog = from.statusId === BACKLOG_ID || target.statusId === BACKLOG_ID;
      if (crossesBacklog && findTask(view, activeId)?.canPlanSprint !== true) {
        toast.warning(t('board.statusDragOnly'));
        return;
      }
      if (!crossesBacklog && from.statusId === target.statusId) return;
    }

    // Rule đổi trạng thái của dự án (APM-TSP-d) — nói trước khi gửi, server vẫn chặn thật.
    const dragged = findTask(view, activeId);
    if (
      dragged &&
      movingFrom &&
      movingTo &&
      !canMoveBetween(dragged, movingFrom.status.category, movingTo.status.category)
    ) {
      toast.warning(
        t(
          dragged.canMove === false ? 'errors.TASK_STATUS_FORBIDDEN' : 'errors.TASK_DONE_FORBIDDEN',
        ),
      );
      return;
    }
    // Chỉ "cần làm" → "đang làm". Mở lại việc đã xong là chuyện khác, xem
    // `checkCanStart` ở server.
    if (movingFrom?.status.category === 'todo' && movingTo?.status.category === 'in_progress') {
      const task = findTask(view, activeId);
      if (task && blocksStart(task)) {
        toast.warning(t('taskStart.TASK_START_NO_ASSIGNEE'));
        return;
      }
      // Thiếu ước lượng thì **vẫn cho đi** — chỉ nhắc. Server cũng trả cảnh báo
      // này trong `warnings[]`, nhưng nói ngay tại đây thì nó tới cùng lúc với
      // cú thả tay thay vì sau một vòng mạng.
      // Dự án bật cửa hạn + ước lượng (APM-EREF) thì popup lo, không nhắc hai lần.
      if (task && warnsBeforeStart(task) && !task.planMissing?.length) {
        toast.warning(t('taskStart.TASK_START_NO_ESTIMATE'));
      }
    }

    /**
     * Kéo qua ranh giới Backlog là đổi **sprint**, không đổi trạng thái.
     *
     * Kéo ra khỏi Backlog: cần một sprint để đưa vào. Không có sprint nào đang
     * chạy thì thao tác này không có nghĩa — nói ra thay vì im lặng bỏ qua,
     * vì thẻ sẽ nhảy về chỗ cũ và người dùng không biết vì sao.
     */
    if (from.statusId === BACKLOG_ID && target.statusId !== BACKLOG_ID) {
      if (!view.sprint) {
        toast.warning(t('board.needSprintFirst'));
        return;
      }
      /*
        Sprint đã đóng thì nói ngay, không gửi gì — server từ chối (thêm việc vào
        sprint đã đóng là sửa lại lịch sử velocity), và trước đây người dùng nhận
        hai toast khó hiểu cho một cú thả (2026-09-28, dự án AGT).
      */
      if (view.sprint.status === 'completed') {
        toast.warning(t('board.sprintIsClosed', { name: view.sprint.name }));
        return;
      }
      // Vào sprint trước, rồi mới đặt trạng thái: ngược lại thì task đổi
      // trạng thái xong vẫn nằm ngoài sprint, và nó biến mất khỏi board.
      const sprintId = view.sprint.id;
      guardStop(
        activeId,
        completesIn(target.statusId),
        (actualStartAt) => {
          /*
          `land` ở đây **cũng** cần, dù `move.onMutate` đã đặt id — APM-BOARD-land.
          Thẻ đang ở cột Backlog, thứ không nằm trong `data.columns`, nên
          `applyMove` không có gì để dời: thẻ chỉ mọc ở cột đích sau khi dữ liệu
          về. Server chậm hơn `LAND_MS` thì id đầu đã bị xoá trước lúc có thẻ.
        */
          /*
          **Tuần tự**, không song song: đổi trạng thái sang một cột của sprint
          khi việc chưa vào sprint thì server trả 400 "cột này thuộc sprint khác".
          Trước đây hai lời gọi chạy cùng lúc, đúng như chú thích ở trên không
          muốn; và vào sprint hỏng thì không đổi trạng thái nữa.
        */
          toSprint.mutate(
            { taskIds: [activeId], sprintId, land: activeId },
            {
              onSuccess: () =>
                moveTask({
                  taskId: activeId,
                  statusId: target.statusId,
                  index: target.index,
                  sameColumn: false,
                  ...(actualStartAt ? { actualStartAt } : {}),
                }),
            },
          );
        },
        undefined,
        enters,
      );
      return;
    }

    if (target.statusId === BACKLOG_ID) {
      /**
       * Truyền `position` — APM-AGILE-d.
       *
       * Thiếu nó thì mọi thẻ thả vào Backlog rơi xuống cuối, và kéo để **xếp
       * hạng** không ăn: `task.position` chính là thứ hạng của product backlog
       * (backlog đã sắp theo nó), nên bỏ trống là tự tay tắt một tính năng mà
       * cả server lẫn dữ liệu đều đã sẵn sàng.
       */
      toSprint.mutate({
        taskIds: [activeId],
        sprintId: null,
        position: persistedDropIndex(view, activeId, BACKLOG_ID, target.index),
        land: activeId,
      });
      return;
    }

    // Thả vào cột Hoàn thành một việc đang bấm giờ ⇒ hỏi trước (APM-TIME-done).
    // Huỷ thì không gọi gì cả — thẻ nằm lại chỗ cũ.
    guardStop(
      activeId,
      from.statusId !== target.statusId && completesIn(target.statusId),
      (actualStartAt) =>
        moveTask({
          taskId: activeId,
          statusId: target.statusId,
          index: target.index,
          sameColumn: from.statusId === target.statusId,
          ...(actualStartAt ? { actualStartAt } : {}),
        }),
      undefined,
      enters,
    );
  }

  /** Cột đích thuộc nhóm `done` — kéo vào đó là làm xong việc. */
  function completesIn(statusId: string): boolean {
    return view.columns.find((column) => column.status.id === statusId)?.status.category === 'done';
  }

  /**
   * Menu của một cột. Thứ tự cột gửi lên là **toàn bộ danh sách** đã đổi chỗ,
   * không phải "cột X sang trái": server nhận trọn thứ tự mới nên không có
   * trạng thái trung gian nào để hai client đọc khác nhau.
   */
  function columnMenu(column: BoardColumnData): ReactNode {
    /**
     * Sắp xếp **trong nhóm của chính cột đó** — cột trong sprint và cột ngoài
     * sprint là hai danh sách riêng.
     *
     * `APM-BOARD-a2` đổi luật ở server: một lượt sắp xếp phải gồm **đủ và đúng**
     * tập cột của một nhóm. Board của một sprint thì vẽ cả hai nhóm cạnh nhau,
     * nên gửi trọn `data.columns` là gửi một danh sách trộn — server từ chối, và
     * người dùng chỉ thấy "Dữ liệu chưa hợp lệ" ở **mọi** cột, không riêng cột
     * vừa tạo.
     *
     * Lọc theo `sprintId` chứ không theo vị trí trên màn hình: hai nhóm vẽ liền
     * nhau nên chỉ số hiển thị không nói được cột nào thuộc nhóm nào.
     */
    const group = columnGroupOf(data.columns, column.status);
    const at = group.findIndex((entry) => entry.status.id === column.status.id);

    return (
      <ColumnMenu
        status={column.status}
        taskCount={column.count}
        canMoveLeft={at > 0}
        canMoveRight={at >= 0 && at < group.length - 1}
        onRename={(name) =>
          columns.mutate({ kind: 'update', statusId: column.status.id, input: { name } })
        }
        onColor={(color) =>
          columns.mutate({ kind: 'update', statusId: column.status.id, input: { color } })
        }
        onCategory={(category) =>
          columns.mutate({ kind: 'update', statusId: column.status.id, input: { category } })
        }
        onWipLimit={(wipLimit) =>
          columns.mutate({ kind: 'update', statusId: column.status.id, input: { wipLimit } })
        }
        onMove={(direction) => {
          const order = reorderColumnWithinGroup(data.columns, column.status.id, direction);
          if (order) columns.mutate({ kind: 'reorder', statusIds: order });
        }}
        onDelete={() => columns.mutate({ kind: 'delete', statusId: column.status.id })}
        inSprint={column.status.sprintId !== null}
        {...(view.sprint || column.status.sprintId !== null
          ? {
              onZoneChange: () =>
                askMoveColumn(
                  column,
                  column.status.sprintId === null ? (view.sprint?.id ?? null) : null,
                ),
            }
          : {})}
      />
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={boardCollision}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setDraggingId(null);
        setOrigin(null);
        setDropAt(null);
        setDragHeight(null);
      }}
    >
      <RemeasureOn
        watch={`${draggingId ?? ''}|${dropAt?.statusId ?? ''}|${dropAt?.slotIndex ?? ''}`}
      />

      <BacklogActions
        count={selectedList.length}
        sprints={sprints}
        pending={toSprint.isPending || createSprintAndMove.isPending}
        onMove={(target) => toSprint.mutate({ taskIds: selectedList, sprintId: target })}
        onCreate={(name, color) =>
          createSprintAndMove.mutate({ name, color, taskIds: selectedList })
        }
        onClear={() => setSelectedIds(new Set())}
      />

      {/*
        **Một hàng ngang**, không hai tầng — APM-BOARD-b.

        Cột người dùng cố ý để chung cho mọi sprint đứng **bên trái**, rồi tới
        khung bao cột của sprint đang xem. Xếp chúng thành hai tầng thì hàng
        dưới trôi khỏi đáy màn hình, mà kéo thẻ giữa hai hàng lại là thao tác
        thường — hai đầu của một cú kéo không được nằm ở hai màn hình cuộn.

        Khung vẫn là khung: nó có viền riêng nên "cột nào của hai tuần này" đọc
        được ngay, chỉ là nó nằm cạnh chứ không nằm dưới.
      */}
      {/*
        Cột cao tới **mép dưới** màn hình.

        Cột chỉ cao bằng nội dung thì cột hai thẻ và cột mười thẻ so le nhau,
        và khoảng trống để thả vào một cột ngắn chỉ còn vài chục pixel — đúng
        cột trống lại là cột người ta hay kéo tới nhất. `min-h` chứ không `h`:
        màn hình thấp vẫn phải cuộn được thay vì cắt mất đáy cột.
      */}
      <MilestoneStrip milestones={milestonesOf(view)} today={today} onOpen={setOpenedId} />

      <div className="mt-2 flex min-h-[calc(100vh-16rem)] items-stretch gap-3 overflow-x-auto pb-2">
        <OutsideZone hasFrame={view.sprint !== null || sprintOptions.length > 0}>
          {outsideColumns.map((column) => renderColumn(column))}
        </OutsideZone>

        {view.sprint || sprintOptions.length > 0 ? (
          <SprintZone sprint={view.sprint}>
            {insideColumns.map((column) => renderColumn(column))}

            {/* Khung rỗng phải nói vì sao rỗng: một cái hộp trống cạnh ba cột
                đầy đọc như một lỗi tải, chứ không đọc như "sprint này chưa có
                cột riêng". Dự án tạo trước APM-BOARD-a2 nằm đúng ở đó. */}
            {insideColumns.length === 0 ? (
              <p className="px-2 py-4 text-xs text-muted-foreground">
                {t('board.sprintFrameNoColumns')}
              </p>
            ) : null}
          </SprintZone>
        ) : null}

        {/*
          **Một** nút thêm cột, không phải một nút mỗi vùng.

          Phạm vi là câu hỏi trong form ("mọi sprint" hay "sprint này"), không
          phải hệ quả của chỗ bấm: hai nút giống hệt nhau ở hai vùng thì người
          dùng bấm cái gần tay hơn và không biết mình vừa chọn gì.
        */}
        {canManageColumns ? (
          <AddColumn
            pending={columns.isPending}
            {...(view.sprint ? { sprintName: view.sprint.name } : {})}
            onCreate={(input, inSprint) =>
              columns.mutate({
                kind: 'create',
                input: inSprint && view.sprint ? { ...input, sprintId: view.sprint.id } : input,
              })
            }
          />
        ) : null}
      </div>

      {/* Thẻ bay theo con trỏ. Không có nó thì thẻ bị cắt bởi `overflow-x-auto`
          của hàng cột ngay khi kéo ra khỏi mép. */}
      {/*
        ⚠️ **Tắt hẳn animation lúc thả** (`dropAnimation={null}`).

        Mặc định dnd-kit cho thẻ nổi bay về ô của node gốc rồi mới biến mất.
        Với một cú kéo qua cột khác thì node gốc không còn ở chỗ cũ nữa, nên
        cái người dùng thấy là thẻ **bay ngược về chỗ xuất phát** sau mỗi lần
        kéo thành công — đọc y như thao tác vừa bị từ chối. `move.onMutate` dời
        thẻ sang đích ngay khi nhả tay, nên không có khoảng trống nào cần một
        animation lấp vào.
      */}
      {complete.dialog}

      <DragOverlay dropAnimation={null}>
        {dragging ? <TaskCard task={dragging} overlay /> : null}
      </DragOverlay>

      {opened && user && (
        <TaskDetailDrawer
          task={opened}
          currentUserId={user.id}
          canEdit={canEditTasks}
          onClose={() => setOpenedId(null)}
          /*
            Cùng khối báo tiến độ với popup của Gantt và của "Việc của tôi" —
            APM-TASK-i. Kéo thẻ sang cột Xong vẫn còn; nút này là đường thứ hai
            cho người đã mở popup ra đọc rồi mới quyết định.
          */
          progress={
            <TaskProgressSlot
              task={opened}
              canEdit={canEditTasks}
              onChange={(progressPct, actualStartAt) =>
                progress.mutate({
                  taskId: opened.id,
                  progressPct,
                  ...(actualStartAt ? { actualStartAt } : {}),
                })
              }
            />
          }
          /*
            Đóng drawer khi mở form: hai lớp phủ chồng nhau thì Esc đóng cái nào
            là chuyện đoán, và cái ở dưới vẫn bắt được cú bấm ra ngoài.
          */
          onEdit={() => {
            setForm({ statusId: null, taskId: opened.id });
            setOpenedId(null);
          }}
        />
      )}

      {form !== null && (
        <TaskFormDialog
          projectId={projectId}
          statusId={form.statusId}
          canApprove={canEditColumns}
          {...(form.taskId === null ? {} : { task: findTask(view, form.taskId) })}
          onClose={() => setForm(null)}
        />
      )}
    </DndContext>
  );
}

/**
 * Bắt dnd-kit **đo lại** khi bố cục cột vừa đổi — APM-BOARD-remeasure.
 *
 * ## Vì sao cần, và vì sao trước đây không cần
 *
 * dnd-kit đo vị trí mọi vùng thả **đúng một lần**, lúc bắt đầu kéo
 * (`MeasuringStrategy.WhileDragging` là mặc định), rồi dùng lại số đo ấy suốt
 * cú kéo. Board này thì đổi bố cục **ngay sau** lúc ấy, hai lần:
 *
 * 1. Thẻ đang nhấc thu về cao 0px ⇒ mọi thẻ dưới nó nhích lên một thân thẻ.
 * 2. `DropSlot` chen vào ⇒ mọi thẻ sau nó nhích xuống một thân thẻ.
 *
 * Nên số đo lệch đúng một thân thẻ ở những đoạn khác nhau của cột, và phép hỏi
 * "con trỏ đang ở trên thẻ nào" trả lời theo một bố cục **không còn tồn tại**.
 * Triệu chứng người dùng báo: không tài nào kéo lên được vị trí trên cùng của
 * cột — vị trí ấy, theo số đo cũ, nằm ở chỗ khác.
 *
 * Trước APM-BOARD-perf4, `SortableContext` âm thầm gọi
 * `measureDroppableContainers` mỗi lần thứ tự `items` đổi, nên số đo còn tạm
 * đúng. Bỏ nó đi thì **không còn ai đo lại** — một thứ mất theo mà không ai
 * nhắc, đúng kiểu hỏng im lặng.
 *
 * ## Đo lại khi nào
 *
 * Chỉ khi bố cục **thật sự** đổi: bắt đầu/kết thúc một cú kéo, và mỗi lần khung
 * trống dời chỗ. Không đo theo từng pixel — đó là điều
 * `MeasuringStrategy.Always` làm, và nó trả giá bằng một loạt
 * `getBoundingClientRect` cho mọi khung hình.
 *
 * ⚠️ Không có vòng lặp: `measureDroppableContainers` làm `DndContext` vẽ lại,
 * nhưng nó không đụng tới `watch`, nên effect này không tự kích hoạt lại.
 */
export function RemeasureOn({ watch }: { readonly watch: string }): ReactNode {
  const { measureDroppableContainers } = useDndContext();

  useEffect(() => {
    // Mảng rỗng = **đo lại tất cả**. Kiểu của dnd-kit khai tham số bắt buộc dù
    // cài đặt có mặc định; truyền tường minh cũng nói rõ ý hơn.
    measureDroppableContainers([]);
  }, [watch, measureDroppableContainers]);

  return null;
}

/**
 * Khung bao quanh cột của sprint đang xem — APM-BOARD-b.
 *
 * Bộ chọn sprint nằm **trên đầu khung**, không ở thanh công cụ chung: khung là
 * thứ nó điều khiển, và đặt nó ở xa thì "đang xem sprint nào" và "những cột này
 * của ai" trở thành hai câu hỏi phải tự nối lại với nhau.
 */
function SprintZone({
  sprint,
  children,
}: {
  readonly sprint: BoardView['sprint'];
  readonly children: ReactNode;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <section
      aria-label={
        sprint ? t('board.sprintFrame', { name: sprint.name }) : t('board.sprintFrameAny')
      }
      className="flex shrink-0 flex-col rounded-DEFAULT border border-border p-2"
      /*
        Viền mang màu của sprint — cùng màu với chấm ở bộ chọn, nên "khung này
        là hai tuần nào" đọc được mà không phải nhìn chữ.
      */
      {...(sprint
        ? {
            style: {
              borderColor: sprintColor(sprint),
              backgroundColor: sprintTint(sprintColor(sprint)),
            },
          }
        : {})}
    >
      {/*
        Chỉ **tên** sprint, không có bộ chọn.

        Bộ chọn nằm trên đầu tab (`TasksPanel`) vì cả ba cách nhìn dùng chung
        nó. Đặt thêm một cái ở đây là hai chỗ bấm cho một giá trị, và người dùng
        sẽ thử xem chúng có khác nhau không — rồi tìm ra rằng có, ở một trạng
        thái nào đó.
      */}
      <header className="flex h-5 flex-wrap items-center gap-2 px-1 pb-2">
        <h3 className="text-sm font-medium">{sprint ? sprint.name : t('board.sprintFrameAny')}</h3>
        {sprint ? (
          <span className="text-xs tabular-nums text-muted-foreground">
            {displayDateRange(sprint.startDate, sprint.endDate)}
          </span>
        ) : (
          // Không chọn sprint thì khung vẫn hiện, nhưng nói rõ nó đang bao cái
          // gì — một cái khung rỗng không lời giải thích đọc như một lỗi.
          <span className="text-xs text-muted-foreground">{t('board.sprintFrameEmpty')}</span>
        )}
      </header>

      <div className="flex min-h-0 flex-1 gap-3">{children}</div>
    </section>
  );
}

/** Cột ngoài khung — có mặt ở **mọi** sprint. Đứng bên trái khung sprint. */
function OutsideZone({
  hasFrame,
  children,
}: {
  readonly hasFrame: boolean;
  readonly children: ReactNode;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <section
      aria-label={t('board.outsideZone')}
      className="flex shrink-0 flex-col gap-1.5 rounded-DEFAULT p-1"
    >
      {/* Chỗ trống bằng đúng header của khung sprint, để hai bên thẳng hàng.
          Không có khung thì không có gì để thẳng hàng cùng. Nhãn giải thích đã
          bỏ (`aria-label` của cả vùng vẫn nói ra điều đó cho trình đọc). */}
      {hasFrame ? <div aria-hidden className="h-5" /> : null}

      <div className="flex min-h-0 flex-1 gap-3">{children}</div>
    </section>
  );
}
